import { useMemo } from "react";
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";
import { usePermission } from "../../../../../hooks/usePermission";
import { useTriggersData } from "../../hooks/TriggersDataContext";
import {
  resolveTriggers,
  computeProjection,
  computeKpiStatus,
  normalizeAuditors,
  computeAnalytics,
} from "./kpiRowUtils";

/**
 * Custom hook that encapsulates ALL KpiRow business logic.
 * Components only receive pre-computed, render-ready data.
 *
 * @param {object} item — KPI question object from the list
 * @returns Computed state for rendering
 */
export const useKpiRowData = (item) => {
  const { currentUserId, currentUser } = useCurrentUser();
  const { userCapabilities = [] } = usePermission();
  const { triggers, fromDate, toDate } = useTriggersData();

  const isAdmin =
    userCapabilities.includes("*") ||
    (currentUserId && Number(currentUserId) === 1) ||
    (currentUser && currentUser.parent_id === null);

  const { triggers: matchingTriggers, isFallback: triggersFallback } = useMemo(
    () => resolveTriggers(triggers, item.id, fromDate, toDate),
    [triggers, item.id, fromDate, toDate]
  );

  const projection = useMemo(
    () => computeProjection(item.analytics?.selected_date_range_value, fromDate, toDate, matchingTriggers),
    [item.analytics?.selected_date_range_value, fromDate, toDate, matchingTriggers]
  );

  const status = useMemo(
    () => computeKpiStatus(item, currentUserId, isAdmin),
    [item, currentUserId, isAdmin]
  );

  const normalizedAuditors = useMemo(
    () => normalizeAuditors(item.auditors),
    [item.auditors]
  );

  const analytics = useMemo(
    () => computeAnalytics(item.analytics),
    [item.analytics]
  );

  const kpiTypes = useMemo(
    () => (Array.isArray(item.kpi_type) ? item.kpi_type.map((t) => t.toUpperCase()) : []),
    [item.kpi_type]
  );

  const canAddAndEditRecords =
    isAdmin || (item?.assigned_to && item.assigned_to.some((user) => String(user.id) === String(currentUserId)));

  return {
    isAdmin,
    currentUserId,
    matchingTriggers,
    triggersFallback,
    projection,
    status,
    normalizedAuditors,
    analytics,
    kpiTypes,
    canAddAndEditRecords,
    fromDate,
    toDate,
  };
};
